"use client";

import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "../../lib";
import { DialKitRange } from "../design-system/dialkit/dialkit-range";
import { FieldShell, type FieldLayoutHint } from "./field-shell";

export interface IntRangeMark {
	value: number;
	label: string;
}

export interface IntRangeFieldProps {
	id?: string;
	prompt: string;
	description?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	name?: string;
	onBlur?: () => void;
	disabled?: boolean;
	min: number;
	max: number;
	step: number;
	currentMin?: number | null;
	currentMax?: number | null;
	marks?: IntRangeMark[];
	formatHint?: string;
	settingType?: string;
	collapsed?: boolean;
	collapsedAction?: ReactNode;
	layoutHint?: FieldLayoutHint;
	className?: string;
	mixed?: boolean;
	mixedTooltip?: ReactNode;
	mixedPlaceholder?: string;
	/** Emits int_range codec `"min-max"`. */
	onSelect?: (settingType: string, value: string) => void;
	onChange?: (range: { min: number; max: number }) => void;
}

function formatMinutes(mins: number): string {
	if (mins < 60) {
		return `${mins}m`;
	}
	const hours = Math.floor(mins / 60);
	const remainder = mins % 60;
	if (remainder === 0) {
		return `${hours}h`;
	}
	return `${hours}h ${remainder}m`;
}

function formatValue(value: number, hint?: string): string {
	if (hint === "minutes") {
		return formatMinutes(value);
	}
	return String(value);
}

/**
 * Dual-bound int range — DialKit Prefer-over (P13.A / C23).
 */
export function IntRangeField({
	id,
	prompt,
	description,
	error,
	reserveErrorSpace = false,
	name,
	onBlur,
	disabled = false,
	min,
	max,
	step,
	currentMin,
	currentMax,
	marks,
	formatHint,
	settingType = "range",
	collapsed = false,
	collapsedAction,
	layoutHint,
	className,
	mixed = false,
	mixedTooltip,
	mixedPlaceholder = "Mixed values",
	onSelect,
	onChange,
}: IntRangeFieldProps) {
	const mixedId = id ? `${id}-mixed` : undefined;
	const initialMin = currentMin ?? min;
	const initialMax = currentMax ?? Math.round(min + (max - min) / 3);
	const [range, setRange] = useState<[number, number]>([
		initialMin,
		initialMax,
	]);

	useEffect(() => {
		const newMin = currentMin ?? min;
		const newMax = currentMax ?? max;
		setRange((prev) => {
			if (prev[0] === newMin && prev[1] === newMax) {
				return prev;
			}
			return [newMin, newMax];
		});
	}, [currentMin, currentMax, min, max]);

	const handleValueChange = useCallback(
		(next: [number, number]) => {
			setRange(next);
			onSelect?.(settingType, `${next[0]}-${next[1]}`);
			onChange?.({ min: next[0], max: next[1] });
		},
		[settingType, onSelect, onChange],
	);

	const rangeMinLabel = useMemo(() => {
		if (marks) {
			const mark = marks.find((m) => m.value === range[0]);
			if (mark) {
				return mark.label;
			}
		}
		return formatValue(range[0], formatHint);
	}, [range, marks, formatHint]);

	const rangeMaxLabel = useMemo(() => {
		if (marks) {
			const mark = marks.find((m) => m.value === range[1]);
			if (mark) {
				return mark.label;
			}
		}
		return formatValue(range[1], formatHint);
	}, [range, marks, formatHint]);

	if (collapsed) {
		return (
			<div
				className={cn(
					"@container/field rounded-xl border bg-card px-4 py-3 text-sm",
					className,
				)}
			>
				<div className="flex items-center justify-between gap-3">
					<div className="min-w-0 flex-1">
						<div className="flex flex-col gap-1 @sm:flex-row @sm:items-center @sm:justify-between @sm:gap-2">
							<span className="text-muted-foreground">{prompt}</span>
							<span className="shrink-0 self-start rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-foreground @sm:self-auto">
								{rangeMinLabel}
								{range[0] !== range[1] && ` → ${rangeMaxLabel}`}
							</span>
						</div>
					</div>
					{collapsedAction}
				</div>
			</div>
		);
	}

	return (
		<FieldShell
			id={id}
			label={prompt}
			description={description}
			error={error}
			reserveErrorSpace={reserveErrorSpace}
			layoutHint={layoutHint}
			className={cn(
				"rounded-xl border bg-card p-4 text-sm",
				disabled && "pointer-events-none opacity-50",
				className,
			)}
			describedById={mixed ? mixedId : undefined}
		>
			{name ? (
				<input
					type="hidden"
					name={name}
					value={`${range[0]}-${range[1]}`}
					readOnly
				/>
			) : null}
			{mixed && mixedTooltip ? (
				<div className="mb-2 text-xs text-muted-foreground">{mixedTooltip}</div>
			) : null}
			{mixed && mixedId ? (
				<p
					id={mixedId}
					data-testid={`${id}-mixed-placeholder`}
					className="mb-2 text-xs text-muted-foreground"
				>
					{mixedPlaceholder}
				</p>
			) : null}
			<div className="px-1" data-field-type="int_range" id={id}>
				<DialKitRange
					label={prompt}
					hideLabel
					min={min}
					max={max}
					step={step}
					value={range}
					onChange={handleValueChange}
					disabled={mixed || disabled}
					aria-invalid={Boolean(error) || undefined}
					onBlur={onBlur}
				/>
			</div>

			{marks && marks.length > 0 ? (
				<div className="relative mx-1 mt-3 h-8">
					{marks.map((mark) => {
						const pct = ((mark.value - min) / (max - min)) * 100;
						const inRange =
							mark.value >= range[0] && mark.value <= range[1];
						return (
							<div
								key={mark.value}
								className={cn(
									"absolute -translate-x-1/2 text-center",
									inRange
										? "text-foreground"
										: "text-muted-foreground/60",
								)}
								style={{ left: `${pct}%` }}
							>
								<span className="block text-[10px] font-medium leading-tight">
									{formatValue(mark.value, formatHint)}
								</span>
								<span className="block text-[10px] leading-tight">
									{mark.label}
								</span>
							</div>
						);
					})}
				</div>
			) : null}

			<div
				className={cn(
					"flex items-center justify-between border-t pt-3 text-xs text-muted-foreground",
					marks && marks.length > 0 ? "mt-3" : "mt-4",
				)}
			>
				<span>
					Range:{" "}
					<span className="font-medium text-foreground">
						{rangeMinLabel}
					</span>
					{range[0] !== range[1] ? (
						<>
							{" → "}
							<span className="font-medium text-foreground">
								{rangeMaxLabel}
							</span>
						</>
					) : null}
				</span>
			</div>
		</FieldShell>
	);
}

/** Alias — same Prefer-over symbol as IntRangeField (P13.A). */
export const RangeSliderField = IntRangeField;
